import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMe, login as loginApi, register as registerApi } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Restore session on mount
  useEffect(() => {
    const restore = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await getMe();
        if (data.success) {
          setUser(data.user);
          setVendor(data.vendor || null);
        }
      } catch {
        localStorage.removeItem('token');
        setToken(null);
      } finally {
        setLoading(false);
      }
    };
    restore();
  }, [token]);

  const saveSession = useCallback((token, user, vendor = null) => {
    localStorage.setItem('token', token);
    setToken(token);
    setUser(user);
    setVendor(vendor);
  }, []);

  const loginUser = useCallback(async (credentials) => {
    const { data } = await loginApi(credentials);
    if (!data.success) throw new Error(data.message || 'Login failed');
    saveSession(data.token, data.user, data.vendor);
    return data;
  }, [saveSession]);

  const registerUser = useCallback(async (formData) => {
    const { data } = await registerApi(formData);
    if (!data.success) throw new Error(data.message || 'Registration failed');
    saveSession(data.token, data.user);
    return data;
  }, [saveSession]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setVendor(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateVendor = useCallback((updates) => {
    setVendor((prev) => (prev ? { ...prev, ...updates } : updates));
  }, []);

  const value = {
    user,
    vendor,
    token,
    loading,
    isAuthenticated: !!user,
    loginUser,
    registerUser,
    logout,
    updateUser,
    updateVendor,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};

export default AuthContext;
